import { combineReducers, configureStore, createAction } from '@reduxjs/toolkit';
import createSagaMiddleware from 'redux-saga';
import alertReducer from './alertSlice';
import loaderReducer from './loaderSlice';
import authReducer from './authSlice';
import insightsReducer from './insightsSlice';
import listReducer from './listSlice';
import filterReducer from './filterSlice';
import userReducer from './userSlice'
import dialogSlice from './dialogSlice';
import filesSlice from './filesSlice';
import productReducer from './productSlice';
import complaintReducer from './complaintSlice';
import securityReducer from './securitySlice';
import emailReducer from './emailSlice';
import whatsappReducer from './whatsappSlice';
import whatsappNewReducer from './slices/whatsappSlice';
import sendWhatsappMessageReducer from './slices/sendMessageSlice'
import campaignsReducer from './slices/campaignsSlice';
import templatesReducer from './slices/templatesSlice';
import analyticsReducer from './slices/analyticsSlice';
import userSaga from './sagas/userSaga';
import categoriesReducer from './categoriesSlice';
import customerReducer from './customerSlice';
import statusAndPriorityReducer from './complaintStatusAndPrioritySlice';
import modelsReducer from './modelsSlice';
import qrReducer from './qrSlice';
import insightsSaga from './sagas/insightsSaga';
import listSaga from './sagas/listSaga';
import categoriesSaga from './sagas/categoriesSaga';
import productSaga from './sagas/productSaga';
import complaintSaga from './sagas/complaintSaga';
import fileUploadSaga from './sagas/fileUploadSaga'
import complaintStatusAndPrioritySaga from './sagas/complaintStatusAndPrioritySaga';
import modelsSaga from './sagas/modelsSaga';
import qrSaga from './sagas/qrSaga';
import emailSaga from './sagas/emailSaga';
import analyticsSaga from './sagas/analyticsSaga';
import whatsappSaga from './sagas/whatsapp';
import whatsappSignupSaga from './sagas/whatsappSignupSaga';
import whatsappSignupReducer from './whatsappSignupSlice';
import companyInfoReducer from './companyInfoSlice';
import companyInfoSaga from './sagas/companyInfoSaga';
import mediaReducer from './mediaSlice';
import entitlementsReducer from './entitlementsSlice';
import entitlementsSaga from './sagas/entitlementsSaga';
import capabilitiesReducer from './capabilitiesSlice';
import capabilitiesSaga from './sagas/capabilitiesSaga';
import accountSetupReducer from './accountSetupSlice';
import accountSetupSaga from './sagas/accountSetupSaga';
import billingReducer from './billingSlice';
import walletReducer from './walletSlice';
import billingSaga from './sagas/billingSaga';
import walletSaga from './sagas/walletSaga';
import invoiceReducer from './invoiceSlice';
import invoiceSaga from './sagas/invoiceSaga';
import navigationReducer from './navigationSlice';
import reportReducer from './reportSlice';
import reportSaga from './sagas/reportSaga';
import whatsappProfileReducer from './whatsappProfileSlice';
import whatsappProfileSaga from './sagas/whatsappProfileSaga';
import whatsappCategoriesReducer from './whatsappCategoriesSlice';
import whatsappCategoriesSaga from './sagas/whatsappCategoriesSaga';
import conversationReducer from './conversationSlice';
import mediaMessageReducer from './mediaMessageSlice';
import mediaUploadSaga from './sagas/mediaUploadSaga';
import conversationSaga from './sagas/conversationSaga';
import { workflowCatalogReducer, workflowDeleteReducer, workflowDetailReducer, workflowListReducer, workflowSaveReducer, workflowPublishReducer, workflowTriggerReducer, workflowArchiveReducer, workflowPauseReducer, workflowCustomActionReducer, workflowAnalyticsReducer, workflowBasicAutomationsReducer } from './slices/workflow';
import { workflowCatalogSaga, workflowDeleteSaga, workflowDetailSaga, workflowListSaga, workflowSaveSaga, workflowPublishSaga, workflowTriggerSaga, workflowArchiveSaga, workflowPauseSaga, workflowCustomActionSaga, workflowAnalyticsSaga, workflowBasicAutomationsSaga } from './sagas/workflow';
import contactsReducer from './slices/contactsSlice';
import contactsSaga from './sagas/contactsSaga';
import businessOnboardingReducer from './businessOnboardingSlice';
import businessOnboardingSaga from './sagas/businessOnboardingSaga';
import { signOut } from '@aws-amplify/auth';
import { registerUnauthorizedHandler } from '../api/unauthorizedHandler';
import { clearStorage } from '../utils/clearStorageHelper';
import { setAuthLoading } from './authSlice';
import { disconnectWhatsAppSocket } from '../hooks/useWhatsAppSocket';
import { setGlobalWhatsAppMode } from '../hooks/useWhatsAppMode';

const sagaMiddleware = createSagaMiddleware();

export const resetAppState = createAction('app/resetAppState');

const appReducer = combineReducers({
    alert: alertReducer,
    loader: loaderReducer,
    auth: authReducer,
    insights: insightsReducer,
    list: listReducer,
    filters: filterReducer,
    user: userReducer,
    categories: categoriesReducer,
    dialogs: dialogSlice,
    files: filesSlice,
    product: productReducer,
    complaint: complaintReducer,
    customer: customerReducer,
    security: securityReducer,
    statusAndPriority: statusAndPriorityReducer,
    models: modelsReducer,
    qr: qrReducer,
    email: emailReducer,
    whatsapp: whatsappReducer,
    whatsappnew: whatsappNewReducer,
    sendMessage: sendWhatsappMessageReducer,
    campaigns: campaignsReducer,
    templates: templatesReducer,
    analytics: analyticsReducer,
    whatsappSignup: whatsappSignupReducer,
    companyInfo: companyInfoReducer,
    media: mediaReducer,
    entitlements: entitlementsReducer,
    capabilities: capabilitiesReducer,
    accountSetup: accountSetupReducer,
    billing: billingReducer,
    wallet: walletReducer,
    invoice: invoiceReducer,
    navigation: navigationReducer,
    report: reportReducer,
    whatsappProfile: whatsappProfileReducer,
    whatsappCategories: whatsappCategoriesReducer,
    conversation: conversationReducer,
    mediaMessage: mediaMessageReducer,
    businessOnboarding: businessOnboardingReducer,
    workflowCatalog: workflowCatalogReducer,
    workflowList: workflowListReducer,
    workflowDetail: workflowDetailReducer,
    workflowSave: workflowSaveReducer,
    workflowDelete: workflowDeleteReducer,
    workflowPublish: workflowPublishReducer,
    workflowArchive: workflowArchiveReducer,
    workflowPause: workflowPauseReducer,
    workflowTrigger: workflowTriggerReducer,
    workflowCustomAction: workflowCustomActionReducer,
    workflowAnalytics: workflowAnalyticsReducer,
    workflowBasicAutomations: workflowBasicAutomationsReducer,
    contacts: contactsReducer,
});

const rootReducer = (state, action) => {
    if (action.type === resetAppState.type) {
        state = undefined;
    }

    return appReducer(state, action);
};

const store = configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware().concat(sagaMiddleware),
});

sagaMiddleware.run(insightsSaga);
sagaMiddleware.run(listSaga);
sagaMiddleware.run(userSaga);
sagaMiddleware.run(categoriesSaga);
sagaMiddleware.run(productSaga);
sagaMiddleware.run(complaintSaga);
sagaMiddleware.run(fileUploadSaga);
sagaMiddleware.run(complaintStatusAndPrioritySaga);
sagaMiddleware.run(modelsSaga);
sagaMiddleware.run(qrSaga);
sagaMiddleware.run(emailSaga);
sagaMiddleware.run(analyticsSaga);
sagaMiddleware.run(whatsappSaga);
sagaMiddleware.run(whatsappSignupSaga);
sagaMiddleware.run(companyInfoSaga);
sagaMiddleware.run(capabilitiesSaga);
sagaMiddleware.run(entitlementsSaga);
sagaMiddleware.run(accountSetupSaga);
sagaMiddleware.run(billingSaga);
sagaMiddleware.run(walletSaga);
sagaMiddleware.run(invoiceSaga);
sagaMiddleware.run(reportSaga);
sagaMiddleware.run(whatsappProfileSaga);
sagaMiddleware.run(whatsappCategoriesSaga);
sagaMiddleware.run(conversationSaga);
sagaMiddleware.run(mediaUploadSaga);
sagaMiddleware.run(workflowCatalogSaga);
sagaMiddleware.run(workflowListSaga);
sagaMiddleware.run(workflowDetailSaga);
sagaMiddleware.run(workflowSaveSaga);
sagaMiddleware.run(workflowDeleteSaga);
sagaMiddleware.run(workflowPublishSaga);
sagaMiddleware.run(workflowTriggerSaga);
sagaMiddleware.run(workflowArchiveSaga);
sagaMiddleware.run(workflowPauseSaga);
sagaMiddleware.run(workflowCustomActionSaga);
sagaMiddleware.run(workflowAnalyticsSaga);
sagaMiddleware.run(workflowBasicAutomationsSaga);
sagaMiddleware.run(contactsSaga);
sagaMiddleware.run(businessOnboardingSaga);

registerUnauthorizedHandler(async () => {
    try {
        await signOut();
    } catch (error) {
        console.error('Error signing out after unauthorized response:', error);
    } finally {
        setGlobalWhatsAppMode('signIn');
        disconnectWhatsAppSocket();
        clearStorage();
        store.dispatch(resetAppState());
        store.dispatch(setAuthLoading(false));
        if (window.location.pathname !== '/') {
            window.location.assign('/');
        }
    }
});

export default store;
